export interface PageMetaDefinition {
  title: string;
  description: string;
  keywords?: string | string[];
  canonicalPath?: string;
  robots?: string;
  openGraph?: {
    title?: string;
    description?: string;
    type?: string;
    image?: string;
    siteName?: string;
  };
  twitter?: {
    card?: string;
    title?: string;
    description?: string;
    image?: string;
  };
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
  extra?: Record<string, string>;
  placeId?: string;
}

export interface DomainMetaConfiguration {
  baseUrl: string;
  default: PageMetaDefinition;
  pages?: Record<string, PageMetaDefinition>;
}

const defaultRobots = 'index, follow';

const royalVapeStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Royal Vape Cyprus',
  url: 'https://www.royalvape.com.cy',
  sameAs: [
    'https://www.facebook.com/RoyalVapeCy',
    'https://www.instagram.com/royalvapecy/'
  ],
};

const titosStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Titos Vape Shop',
  url: 'https://www.titosecig.com',
};

const royalTradingStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Royal Trading Cyprus',
  url: 'https://royaltradingcy.com',
};

const royalHausStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Royal Hause Cyprus',
  url: 'https://www.royalhaus.com.cy',
};

export const domainMetaConfig: Record<string, DomainMetaConfiguration> = {
  default: {
    baseUrl: 'https://www.royalvape.com.cy',
    default: {
      title: 'Vape Shop Cyprus | Premium E-Cigarettes & Liquids',
      description:
        'Discover premium vape devices, disposables, and liquids delivered across Cyprus. Enjoy expert advice, fast shipping, and great prices online.',
      keywords: 'cyprus vape shop, premium e-cigarettes, disposable vapes, e-liquids cyprus, vape accessories',
      canonicalPath: '/',
      robots: defaultRobots,
      openGraph: {
        title: 'Vape Shop Cyprus | Premium E-Cigarettes & Liquids',
        description:
          'Discover premium vape devices, disposables, and liquids delivered across Cyprus. Enjoy expert advice, fast shipping, and great prices online.',
        type: 'website',
        siteName: 'Vape Shop Cyprus',
      },
      twitter: {
        card: 'summary_large_image',
        title: 'Vape Shop Cyprus | Premium E-Cigarettes & Liquids',
        description:
          'Discover premium vape devices, disposables, and liquids delivered across Cyprus. Enjoy expert advice, fast shipping, and great prices online.',
      },
    },
  },
  'www.royalvape.com.cy': {
    baseUrl: 'https://www.royalvape.com.cy',
    default: {
      title: 'Royal Vape Cyprus | Premium E-Cigarettes & Vapes',
      description:
        'Shop authentic vape devices, disposables, and e-liquids with free delivery across Cyprus. Expert staff and five retail stores ready to help.',
      keywords: 'royal vape cyprus, vape shop cyprus, premium e-cigarettes, disposable vapes, vape delivery cyprus',
      canonicalPath: '/',
      robots: defaultRobots,
      openGraph: {
        title: 'Royal Vape Cyprus | Premium E-Cigarettes & Vapes',
        description:
          'Shop authentic vape devices, disposables, and e-liquids with free delivery across Cyprus. Expert staff and five retail stores ready to help.',
        type: 'website',
        siteName: 'Royal Vape Cyprus',
      },
      twitter: {
        card: 'summary_large_image',
        title: 'Royal Vape Cyprus | Premium E-Cigarettes & Vapes',
        description:
          'Shop authentic vape devices, disposables, and e-liquids with free delivery across Cyprus. Expert staff and five retail stores ready to help.',
      },
      structuredData: royalVapeStructuredData,
      extra: {
        company: 'Royal Vape Ltd',
      },
      //placeId: 'ChIJ99Geh5AZ3hQRQRUAWWMO9dA', now it is using the application param iq_web_google_reviews_api 
    },
    pages: {
      about: {
        title: 'About Royal Vape Cyprus | Our Story & Mission',
        description:
          'Learn how Royal Vape became Cyprus go-to vape retailer, delivering quality devices, guidance, and customer care since our first Nicosia store.',
        keywords: 'royal vape about, vape shop story, royal vape mission, cyprus vape experts',
        canonicalPath: '/about',
        robots: defaultRobots,
        openGraph: {
          title: 'About Royal Vape Cyprus | Who We Are',
          description:
            'Learn how Royal Vape became Cyprus go-to vape retailer, delivering quality devices, guidance, and customer care since our first Nicosia store.',
          type: 'article',
          siteName: 'Royal Vape Cyprus',
        },
      },
      contact: {
        title: 'Royal Vape Cyprus | Contact Our Vape Experts',
        description:
          'Need advice on vape devices or flavours? Reach the Royal Vape team by phone, email, or by visiting one of our Cyprus stores.',
        keywords: 'royal vape contact, vape support cyprus, royal vape customer service, vape shop contact details',
        canonicalPath: '/contact',
        robots: defaultRobots,
        openGraph: {
          title: 'Royal Vape Cyprus | Contact Us',
          description:
            'Need advice on vape devices or flavours? Reach the Royal Vape team by phone, email, or by visiting one of our Cyprus stores.',
          type: 'website',
          siteName: 'Royal Vape Cyprus',
        },
      },
      products: {
        title: 'Shop Vapes & E-Liquids Online | Royal Vape Cyprus',
        description:
          'Browse Royal Vapes full online range of kits, pods, disposables, and e-liquids. Enjoy fast delivery and secure checkout in Cyprus.',
        keywords: 'royal vape products, cyprus vape catalogue, royal vape devices, vape liquids cyprus',
        canonicalPath: '/products',
        robots: defaultRobots,
        openGraph: {
          title: 'Royal Vape Cyprus | Online Vape Store',
          description:
            'Browse Royal Vapes full online range of kits, pods, disposables, and e-liquids. Enjoy fast delivery and secure checkout in Cyprus.',
          type: 'website',
          siteName: 'Royal Vape Cyprus',
        },
      },
      partner: {
        title: 'Become a Royal Vape Partner | Wholesale & Retail',
        description:
          'Join the Royal Vape partner network and access wholesale pricing, training, and support for your vape business in Cyprus.',
        keywords: 'royal vape partner, vape franchise cyprus, vape wholesale partner, royal vape business support',
        canonicalPath: '/partner',
        robots: defaultRobots,
        openGraph: {
          title: 'Royal Vape Cyprus | Become a Partner',
          description:
            'Join the Royal Vape partner network and access wholesale pricing, training, and support for your vape business in Cyprus.',
          type: 'website',
          siteName: 'Royal Vape Cyprus',
        },
      },
    },
  },
  'www.titosecig.com': {
    baseUrl: 'https://www.titosecig.com',
    default: {
      title: 'Titos Vape Shop | Cyprus Vape Devices & Liquids',
      description:
        'Explore premium vape kits, disposables, and flavourful e-liquids from Titos Vape Shop in Limassol with fast, reliable delivery across Cyprus.',
      keywords: 'titos vape shop, limassol vape shop, cyprus e-liquids, vape kits limassol, disposable vapes cyprus',
      canonicalPath: '/',
      robots: defaultRobots,
      openGraph: {
        title: 'Titos Vape Shop | Cyprus Vape Devices & Liquids',
        description:
          'Explore premium vape kits, disposables, and flavourful e-liquids from Titos Vape Shop in Limassol with fast, reliable delivery across Cyprus.',
        type: 'website',
        siteName: 'Titos Vape Shop',
      },
      twitter: {
        card: 'summary_large_image',
        title: 'Titos Vape Shop | Cyprus Vape Devices & Liquids',
        description:
          'Explore premium vape kits, disposables, and flavourful e-liquids from Titos Vape Shop in Limassol with fast, reliable delivery across Cyprus.',
      },
      structuredData: titosStructuredData,
      extra: {
        company: 'The Vape Shop',
      },
      placeId: '', //ChIJd-Yo9EMz5xQRTX8GtHsGg-I
    },
    pages: {
      about: {
        title: 'About Titos Vape Shop | Limassol Vape Experts',
        description:
          'Find out how Titos Vape Shop has served Cyprus vapers since 2011 with quality gear, fair prices, and personalised support.',
        keywords: 'titos vape about, vape shop story, titos vape mission, cyprus vape experts',
        canonicalPath: '/about',
        robots: defaultRobots,
        openGraph: {
          title: 'About Titos Vape Shop',
          description:
            'Find out how Titos Vape Shop has served Cyprus vapers since 2011 with quality gear, fair prices, and personalised support.',
          type: 'article',
          siteName: 'Titos Vape Shop',
        },
      },
      contact: {
        title: 'Contact Titos Vape Shop | We Are Here to Help',
        description:
          'Message, call, or visit Titos Vape Shop in Limassol for guidance on vape devices, flavours, and accessories.',
        keywords: 'titos vape contact, vape support cyprus, titos vape customer service, titos shop contact details',
        canonicalPath: '/contact',
        robots: defaultRobots,
        openGraph: {
          title: 'Contact Titos Vape Shop',
          description:
            'Message, call, or visit Titos Vape Shop in Limassol for guidance on vape devices, flavours, and accessories.',
          type: 'website',
          siteName: 'Titos Vape Shop',
        },
      },
      products: {
        title: 'Shop Online | Titos Vape Devices, Pods & Liquids',
        description:
          'Browse the complete Titos Vape Shop catalogue of vape devices, pods, coils, and e-liquids with delivery throughout Cyprus.',
        keywords: 'titos vape products, cyprus vape catalogue, titos vape devices, vape liquids cyprus',
        canonicalPath: '/products',
        robots: defaultRobots,
        openGraph: {
          title: 'Titos Vape Shop | Online Catalogue',
          description:
            'Browse the complete Titos Vape Shop catalogue of vape devices, pods, coils, and e-liquids with delivery throughout Cyprus.',
          type: 'website',
          siteName: 'Titos Vape Shop',
        },
      },
      partner: {
        title: 'Partner with Titos Vape Shop | Wholesale Vaping',
        description:
          'Collaborate with Titos Vape Shop for wholesale vape supply, private label opportunities, and expert support across Cyprus.',
        keywords: 'titos vape partner, vape franchise cyprus, vape wholesale partner, titos vape business support',
        canonicalPath: '/partner',
        robots: defaultRobots,
        openGraph: {
          title: 'Titos Vape Shop | Become a Partner',
          description:
            'Collaborate with Titos Vape Shop for wholesale vape supply, private label opportunities, and expert support across Cyprus.',
          type: 'website',
          siteName: 'Titos Vape Shop',
        },
      },
    },
  },
  'royaltradingcy.com': {
    baseUrl: 'https://royaltradingcy.com',
    default: {
      title: 'Royal Trading Cyprus | Vape Wholesale Specialists',
      description:
        'Royal Trading Cyprus supplies retailers with reliable vape hardware, disposables, and e-liquids at competitive wholesale prices.',
      keywords: 'royal trading cyprus, vape wholesale cyprus, e-liquid distribution, vape hardware wholesale, b2b vaping cyprus',
      canonicalPath: '/',
      robots: defaultRobots,
      openGraph: {
        title: 'Royal Trading Cyprus | Vape Wholesale Specialists',
        description:
          'Royal Trading Cyprus supplies retailers with reliable vape hardware, disposables, and e-liquids at competitive wholesale prices.',
        type: 'website',
        siteName: 'Royal Trading Cyprus',
      },
      twitter: {
        card: 'summary_large_image',
        title: 'Royal Trading Cyprus | Vape Wholesale Specialists',
        description:
          'Royal Trading Cyprus supplies retailers with reliable vape hardware, disposables, and e-liquids at competitive wholesale prices.',
      },
      structuredData: royalTradingStructuredData,
      extra: {
        company: 'Royal Trading Cyprus',
      },
    },
    pages: {
      about: {
        title: 'About Royal Trading Cyprus | Wholesale Vape Supply',
        description:
          'Discover the story behind Royal Trading Cyprus and how we support retailers with quality vaping products and dependable service.',
        canonicalPath: '/about',
        robots: defaultRobots,
        openGraph: {
          title: 'About Royal Trading Cyprus',
          description:
            'Discover the story behind Royal Trading Cyprus and how we support retailers with quality vaping products and dependable service.',
          type: 'article',
          siteName: 'Royal Trading Cyprus',
        },
      },
      contact: {
        title: 'Contact Royal Trading Cyprus | Wholesale Enquiries',
        description:
          'Reach the Royal Trading Cyprus team for wholesale vape supply enquiries, account support, and partnership requests.',
        canonicalPath: '/contact',
        robots: defaultRobots,
        openGraph: {
          title: 'Contact Royal Trading Cyprus',
          description:
            'Reach the Royal Trading Cyprus team for wholesale vape supply enquiries, account support, and partnership requests.',
          type: 'website',
          siteName: 'Royal Trading Cyprus',
        },
      },
      products: {
        title: 'Wholesale Vape Catalogue | Royal Trading Cyprus',
        description:
          'Review our wholesale vape catalogue featuring devices, pods, coils, and e-liquids sourced for Cyprus retailers.',
        canonicalPath: '/products',
        robots: defaultRobots,
        openGraph: {
          title: 'Royal Trading Cyprus | Product Catalogue',
          description:
            'Review our wholesale vape catalogue featuring devices, pods, coils, and e-liquids sourced for Cyprus retailers.',
          type: 'website',
          siteName: 'Royal Trading Cyprus',
        },
      },
      partner: {
        title: 'Partner with Royal Trading Cyprus | Distributor Network',
        description:
          'Apply to become a Royal Trading Cyprus partner and access exclusive brands, wholesale pricing, and dependable distribution.',
        canonicalPath: '/partner',
        robots: defaultRobots,
        openGraph: {
          title: 'Royal Trading Cyprus | Become a Partner',
          description:
            'Apply to become a Royal Trading Cyprus partner and access exclusive brands, wholesale pricing, and dependable distribution.',
          type: 'website',
          siteName: 'Royal Trading Cyprus',
        },
      },
    },
  },
  'www.royalhaus.com.cy': {
    baseUrl: 'https://www.royalhaus.com.cy',
    default: {
      title: 'Royal Haus | Affordable Royalty , Intelligent Living',
      description:
        'Royalhaus was created with a vision to redefine modern living by blending innovation, comfort, and timeless design. We specialize in smart home furniture and premium house renovation materials, offering products that seamlessly combine functionality with style',
      keywords: 'royal haus, furnitures, smart home furnitures, renovation materials, house renovation, house renovation cyprus',
      canonicalPath: '/',
      robots: defaultRobots,
      openGraph: {
        title: 'Royal Haus | Affordable Royalty , Intelligent Living',
        description:
          'Royal Haus was created with a vision to redefine modern living by blending innovation, comfort, and timeless design. We specialize in smart home furniture and premium house renovation materials, offering products that seamlessly combine functionality with style',
        type: 'website',
        siteName: 'Royal Haus',
      },
      twitter: {
        card: 'summary_large_image',
        title: 'Royal Haus | Affordable Royalty , Intelligent Living',
        description:
          'Royal Haus was created with a vision to redefine modern living by blending innovation, comfort, and timeless design. We specialize in smart home furniture and premium house renovation materials, offering products that seamlessly combine functionality with style',
      },
      structuredData: royalHausStructuredData,
      extra: {
        company: 'Royal Haus',
      },
    },
    pages: {
      about: {
        title: 'About Royal Haus | Affordable Royalty , Intelligent Living',
        description:
          'Discover the story behind Royal Haus and how we support our customers with our intelligent furnitures and service.',
        canonicalPath: '/about',
        robots: defaultRobots,
        openGraph: {
          title: 'About Royal Haus',
          description:
            'Discover the story behind Royal Haus and how we support our customers with our intelligent furnitures and service.',
          type: 'article',
          siteName: 'Royal Trading Cyprus',
        },
      },
      contact: {
        title: 'Contact Royal Hause Cyprus | Affordable Royalty , Intelligent Living',
        description:
          'Discover the story behind Royal Haus and how we support our customers with our intelligent furnitures and service.',
        canonicalPath: '/contact',
        robots: defaultRobots,
        openGraph: {
          title: 'Contact Royal Trading Cyprus',
          description:
            'Discover the story behind Royal Haus and how we support our customers with our intelligent furnitures and service.',
          type: 'website',
          siteName: 'Royal Trading Cyprus',
        },
      },
      products: {
        title: 'Royal Haus Intelligent Furnitures | Affordable Royalty , Intelligent Living',
        description:
          'Review our intelligent products catalogue featuring smart furniture, kithcen sinkg, flooring, wall panels and sanitary ware.',
        keywords: 'smart furniture, intelligent furniture, renovation material, house renovation, flooring, wall panels, sanitary ware',
        canonicalPath: '/products',
        robots: defaultRobots,
        openGraph: {
          title: 'Royal Haus Intelligent Furnitures | Affordable Royalty , Intelligent Living',
          description:
            'Review our intelligent products catalogue featuring smart furniture, kithcen sinkg, flooring, wall panels and sanitary ware.',
          type: 'website',
          siteName: 'Royal Haus Cyprus',
        },
      },
      partner: {
        title: 'Partner with Royal Haus Cyprus | Distributor Network',
        description:
          'Apply to become a Royal Haus Cyprus partner and access exclusive smart furniture.',        
        canonicalPath: '/partner',
        robots: defaultRobots,
        openGraph: {
          title: 'Royal Haus Cyprus | Become a Partner',
          description:
            'Apply to become a Royal Haus Cyprus partner and access exclusive brands, wholesale pricing, and dependable distribution.',
          type: 'website',
          siteName: 'Royal Haus Cyprus',
        },
      },
    },
  },
  localhost: {
    baseUrl: 'http://localhost:4200',
    default: {
      title: 'Local TEST Vape Demo | Angular Multi-Store',
      description:
        'Developer preview of the Angular multi-store vape platform. Configure domain-specific content, products, and SEO metadata.',
      keywords: 'TEST,angular vape demo, multi-store angular, seo metadata demo, storefront sandbox, developer preview',
      canonicalPath: '/',
      robots: 'noindex, nofollow',
      openGraph: {
        title: 'Local Vape Demo | Angular Multi-Store',
        description:
          'Developer preview of the Angular multi-store vape platform. Configure domain-specific content, products, and SEO metadata.',
        type: 'website',
        siteName: 'Local Vape Demo',
      },
      twitter: {
        card: 'summary',
        title: 'Local Vape Demo | Angular Multi-Store',
        description:
          'Developer preview of the Angular multi-store vape platform. Configure domain-specific content, products, and SEO metadata.',
      },
      extra: {
        company: 'Localhost Dev',
      },
      placeId: '',
    },
    pages: {
      about: {
        title: 'About This Demo Store',
        description:
          'Sandbox environment used for testing the Angular multi-store experience and SEO setup.',          
        keywords: 'demo store, angular multi-store, seo test, developer sandbox, vape shop demo',
        canonicalPath: '/about',
        robots: 'noindex, nofollow',

      },
      contact: {
        title: 'Contact The Demo Team',
        description:
          'Demo contact page placeholder. Replace with production details before launch.',
        keywords: 'test contact us page',
        canonicalPath: '/contact',
        robots: 'noindex, nofollow',
      },
      products: {
        title: 'Demo Product Catalogue',
        description:
          'Preview the demo product catalogue used for development and integration testing.',
        canonicalPath: '/products',
        robots: 'noindex, nofollow',
      },
      partner: {
        title: 'Demo Partner Programme',
        description:
          'Placeholder copy for the partner programme page while developing the platform.',
        canonicalPath: '/partner',
        robots: 'noindex, nofollow',
      },
    },
  },
};

export type DomainKey = keyof typeof domainMetaConfig;

export const DEFAULT_DOMAIN_KEY: DomainKey = 'default';
export const DEFAULT_PAGE_KEY = 'default';
